<template>
  <v-card class="pb-8">
    <!-- Search Bar -->
    <v-card-title>
      <v-row justify="end">
        <v-col cols="12" sm="4" class="search-bar-container">
          <div class="d-flex">
            <AdvisorSearchInput
              :value="searchText"
              @search="onSearchFromChild"
              @clear="clearSearch"
            />
          </div>
        </v-col>
      </v-row>
    </v-card-title>

    <!-- Filter Tabs -->
    <div class="custom-tabs">
      <div
        v-for="tab in filterTabs"
        :key="tab.value"
        class="custom-tab"
        :class="{
          'datatable-bg-light-green':
            activeTab === 'list_all' && tab.value === 'list_all',
          'datatable-bg-pink':
            activeTab === 'complete_steps' && tab.value === 'complete_steps',
          'datatable-bg-blue':
            activeTab === 'download_wills' && tab.value === 'download_wills',
          'datatable-bg-orange':
            activeTab === 'sign_upload_wills' &&
            tab.value === 'sign_upload_wills',
          'datatable-bg-green':
            activeTab === 'validated_wills' && tab.value === 'validated_wills',
          'datatable-bg-in-storage':
            activeTab === 'in_storage' && tab.value === 'in_storage',
          active: activeTab === tab.value,
        }"
        @click="$emit('update:active-tab', tab.value)"
      >
        {{ tab.text }}
      </div>
    </div>

    <!-- Data Table -->
    <v-data-table
      class="admin-list-table"
      :headers="headers"
      :items="filteredTestators"
      :loading="datatableLoading"
      :options="options"
      :server-items-length="totalTestators"
      :footer-props="footerProps"
      @update:options="onOptionsUpdate"
      @click:row="redirectToSummary"
    >
      <template v-slot:footer.prepend>
        <v-progress-linear v-if="datatableLoading" indeterminate height="2" />
      </template>

      <template #[`item.fullName`]="{ item }">
        {{ item.firstNames }} {{ item.lastName }}
      </template>

      <template
        v-if="activeTab !== 'in_storage'"
        #[`item.nextRequiredAction`]="{ item, index }"
      >
        <div class="action-row">
          <div class="icon-wrapper">
            <v-img
              :src="
                require(`@/assets/shared/advisor-dashboard/icons/${getActionIcon(
                  item.nextRequiredAction
                )}`)
              "
              width="20"
              height="20"
              contain
            />
          </div>
          <span class="action-text">{{ item.nextRequiredAction }}</span>
        </div>
      </template>

      <template #[`item.lastLogin`]="{ item }">
        {{ item.lastLogin | date_DMMMYYYY }}
      </template>

      <template #[`item.createdAt`]="{ item }">
        <span
          v-if="
            activeTab === 'validated_wills' && item.uploadStatus === 'VALID'
          "
        >
          {{ item.uploadUpdatedAt | date_DMMMYYYY }}
        </span>
        <span v-else>
          {{ item.createdAt | date_DMMMYYYY }}
        </span>
      </template>

      <template
        v-if="activeTab === 'in_storage'"
        #[`item.dateStored`]="{ item }"
      >
        <span>{{ item.vaultStored | date_DMMMYYYY }}</span>
      </template>

      <template
        v-if="activeTab === 'validated_wills'"
        #[`item.remindUser`]="{ item }"
      >
        <div class="d-flex align-center justify-center">
          <v-chip v-if="item.willReadyToCollect" color="green" small dark>
            Collection booked
          </v-chip>
          <span v-else class="text-caption text--secondary">
            Ready to Schedule Collection
          </span>
        </div>
      </template>
      <template
        v-else-if="activeTab !== 'in_storage'"
        #[`item.remindUser`]="{ item }"
      >
        <div class="d-flex align-center">
          <a
            :href="`mailto:${item.email}`"
            class="email-link"
            @click.stop
            tabindex="-1"
          >
            <v-icon small class="mr-1">
              {{ item.doNotContact ? icons.mdiDoNotDisturb : icons.mdiEmail }}
            </v-icon>
            <span class="caption">Email User</span>
          </a>
        </div>
      </template>
      <template
        v-if="['list_all', 'complete_steps'].includes(activeTab)"
        #[`item.numberOfCompletedSteps`]="{ item }"
      >
        <v-chip :color="stepsColor(item.numberOfCompletedSteps)" dark>
          {{ item.numberOfCompletedSteps }}
        </v-chip>
      </template>
    </v-data-table>
    <!--
      Temporarily hidden (OMW-433): the bulk booking books a separate Courier
      Guy collection per testator at the same address. Re-enable once the
      shared-collection version of the booking flow is live.
    -->
    <GradientButton
      v-if="activeTab === 'validated_wills' && canBookCollection"
      class="mt-5 mb-5 ml-8"
      :title="'Book Collection'"
      @gradientButtonClicked="openCollectionDialog"
    />

    <WillCollectionBookingDialog
      v-model="collectionDialogOpen"
      mode="advisor"
      @booking-success="onCollectionBooked"
    />

    <DryRunRequestsDialog
      v-model="dryRunPreviewOpen"
      :requests="dryRunPreviewRequests"
    />

    <v-snackbar
      v-model="collectionBookedSnackbar"
      color="success"
      :timeout="6000"
    >
      {{ collectionBookedMessage }}
    </v-snackbar>
  </v-card>
</template>

<script>
  import {
    ADVISOR_TESTATORS_OPTIMIZED_QUERY,
    ADVISOR_TESTATOR_ACTION_COUNTS_OPTIMIZED_QUERY,
    ADVISOR_COLLECTION_ELIGIBLE_TESTATORS_QUERY,
  } from '../../constants/graphql/testators';
  import AdvisorSearchInput from './AdvisorSearchInput.vue';
  import WillCollectionBookingDialog from '../common/will_collection_bookings/WillCollectionBookingDialog.vue';
  import DryRunRequestsDialog from '../common/will_collection_bookings/DryRunRequestsDialog.vue';
  import { whenNoDialogIsActive } from '../../helpers/dialogStacking';

  const ITEMS_PER_PAGE_OPTIONS = [10, 25, 100, 500];

  export default {
    name: 'AdvisorDatatableOptimized',
    components: {
      AdvisorSearchInput,
      WillCollectionBookingDialog,
      DryRunRequestsDialog,
    },
    props: {
      company: {
        type: Object,
        default: () => {
          return {};
        },
      },
      searchAdvisorEmail: {
        type: String,
        default: null,
      },
      activeTab: {
        type: String,
        default: 'list_all',
      },
    },
    data() {
      return {
        searchText: '',
        allTestators: [],
        loading: true,
        datatableLoading: false,
        allTestatorsLoading: false,
        options: {
          page: 1,
          itemsPerPage: 10,
          sortBy: [],
          sortDesc: [],
        },
        totalTestators: 0,
        searchQueryParam: '',
        collectionDialogOpen: false,
        BOOK_COLLECTION_ENABLED: false,
        collectionBookedSnackbar: false,
        collectionBookedDryRun: false,
        collectionBookedLinked: false,
        eligibleCollectionTestators: [],
        collectionBookedCount: 0,
        dryRunPreviewOpen: false,
        dryRunPreviewRequests: '',

        actionCountsData: {
          complete_steps: 0,
          download_wills: 0,
          sign_upload_wills: 0,
          validated_wills: 0,
          in_storage: 0,
        },
        filterTabs: [
          { text: 'List All', value: 'list_all' },
          { text: 'Complete Steps', value: 'complete_steps' },
          { text: 'Download Wills', value: 'download_wills' },
          { text: 'Sign and Upload Wills', value: 'sign_upload_wills' },
          { text: 'Validated Wills', value: 'validated_wills' },
          { text: 'In Storage', value: 'in_storage' },
        ],
      };
    },
    beforeDestroy() {},
    computed: {
      headers() {
        const isValidatedWillsTab = this.activeTab === 'validated_wills';
        const isInStorageTab = this.activeTab === 'in_storage';
        const showStepsCompleted = ['list_all', 'complete_steps'].includes(
          this.activeTab
        );

        let baseHeaders = [];
        if (showStepsCompleted) {
          baseHeaders.push({
            text: 'Step',
            value: 'numberOfCompletedSteps',
            sortable: true,
            width: '10%',
            fixed: true,
          });
        }
        baseHeaders = baseHeaders.concat([
          {
            text: 'Full Name',
            value: 'fullName',
            sortable: true,
            width: '25%',
            fixed: true,
          },
          {
            text: 'Next Required Action',
            value: 'nextRequiredAction',
            sortable: this.activeTab === 'list_all',
            width: '30%',
            fixed: true,
          },
          {
            text: 'Last Login',
            value: 'lastLogin',
            sortable: true,
            width: '15%',
            fixed: true,
          },
          {
            text: isValidatedWillsTab
              ? 'Date Validated'
              : 'Invite / Signup Date',
            value: 'createdAt',
            sortable: true,
            width: isValidatedWillsTab ? '12%' : '20%',
            fixed: true,
          },
          {
            text: isValidatedWillsTab
              ? 'Wills in my possession'
              : isInStorageTab
              ? 'Date Stored'
              : 'Remind User',
            value: 'remindUser',
            sortable: false,
            align: 'center',
            width: isValidatedWillsTab ? '18%' : '10%',
            fixed: true,
          },
        ]);

        if (isInStorageTab) {
          const offset = showStepsCompleted ? 1 : 0;
          return [
            ...(showStepsCompleted ? [baseHeaders[0]] : []),
            baseHeaders[offset],
            baseHeaders[offset + 2],
            baseHeaders[offset + 3],
            {
              text: 'Date Stored',
              value: 'dateStored',
              sortable: false,
              align: 'center',
              width: '10%',
              fixed: true,
            },
          ];
        }
        return baseHeaders;
      },
      filteredTestators() {
        return this.allTestators;
      },
      actionCounts() {
        return this.actionCountsData;
      },
      allTestatorsVariables() {
        return this.queryVariables();
      },
      footerProps() {
        return {
          itemsPerPageOptions: ITEMS_PER_PAGE_OPTIONS,
        };
      },
      canBookCollection() {
        return this.eligibleCollectionTestators.length > 0;
      },
      collectionBookedMessage() {
        const count = this.collectionBookedCount;
        const wills = count === 1 ? '1 will' : `${count} wills`;
        if (this.collectionBookedLinked) {
          return `${wills} added to the collection already booked for that address. Hand them all to the courier.`;
        }
        return this.collectionBookedDryRun
          ? `Collection for ${wills} recorded. No request was sent to The Courier Guy on this server.`
          : `Collection booked for ${wills}. Hand them all to The Courier Guy on the date selected.`;
      },
    },
    watch: {
      allTestators() {
        this.updateDatatableLoading();
      },
      searchAdvisorEmail(newVal) {
        if (newVal && this.searchQueryParam !== newVal) {
          this.searchText = newVal;
          this.searchQueryParam = newVal;
          this.options.page = 1;
          this.loading = true;
        }
      },
      activeTab: {
        handler() {
          this.allTestators = [];
          this.options.page = 1;
          this.loading = true;
        },
      },
      actionCounts: {
        handler(newCounts) {
          this.$emit('update:counts', newCounts);
        },
        immediate: true,
      },
    },
    methods: {
      buildVariables(overrides = {}) {
        return { ...this.queryVariables(), ...overrides };
      },
      updateDatatableLoading() {
        const isLoading = this.allTestatorsLoading;
        const hasRows =
          Array.isArray(this.allTestators) && this.allTestators.length > 0;
        this.datatableLoading = isLoading && !hasRows;
      },
      redirectToSummary(item) {
        this.$router.push('advisors/will-summary/' + item.id);
      },
      getActionColor(item) {
        switch (item.nextRequiredAction) {
          case 'Send Will For Storage':
            return 'teal';
          case 'Download Will':
            return 'blue';
          case 'Sign And Upload Will':
            return 'orange';
          case 'Complete Will Steps':
            return 'pink';
          default:
            return 'grey';
        }
      },
      getActionIcon(action) {
        switch (action) {
          case 'Send Will For Storage':
            return 'green.svg';
          case 'Download Will':
            return 'blue.svg';
          case 'Sign And Upload Will':
            return 'orange.svg';
          case 'Complete Will Steps':
            return 'pink.svg';
          case 'In Storage':
            return 'green.svg';
          default:
            return 'green.svg';
        }
      },
      searchData() {
        if (this.searchQueryParam !== this.searchText) {
          this.searchQueryParam = this.searchText;
          this.options.page = 1;
          this.loading = true;
        }
      },
      onOptionsUpdate(newOptions) {
        const allowed = ITEMS_PER_PAGE_OPTIONS.includes(newOptions.itemsPerPage)
          ? newOptions.itemsPerPage
          : ITEMS_PER_PAGE_OPTIONS[0];

        const pageChanged = newOptions.page !== this.options.page;
        const sortChanged =
          JSON.stringify(newOptions.sortBy) !==
            JSON.stringify(this.options.sortBy) ||
          JSON.stringify(newOptions.sortDesc) !==
            JSON.stringify(this.options.sortDesc);
        const perPageChanged = allowed !== this.options.itemsPerPage;

        this.options = {
          ...this.options,
          ...newOptions,
          itemsPerPage: allowed,
        };
        this.loading = true;

        if (pageChanged || sortChanged || perPageChanged) {
          this.allTestators = [];
        }
      },
      onSearchFromChild(trimmedValue) {
        const searchParam = trimmedValue === '' ? null : trimmedValue;
        if (this.searchQueryParam !== searchParam) {
          this.allTestators = [];
          this.searchText = trimmedValue;
          this.searchQueryParam = searchParam;
          this.options.page = 1;
          this.loading = true;
        }
      },
      clearSearch() {
        if (this.searchQueryParam !== null) {
          this.allTestators = [];
          this.searchText = '';
          this.searchQueryParam = null;
          this.options.page = 1;
          this.loading = true;
        }
      },
      queryVariables() {
        const rawLimit = this.options.itemsPerPage;
        const limit = ITEMS_PER_PAGE_OPTIONS.includes(rawLimit)
          ? rawLimit
          : ITEMS_PER_PAGE_OPTIONS[0];
        const page = this.options.page || 1;
        const search = this.searchQueryParam || null;
        let sortBy = null;
        let sortDesc = true;
        if (this.options.sortBy && this.options.sortBy.length) {
          sortBy = this.options.sortBy[0];
          sortDesc = this.options.sortDesc[0];
        }

        return {
          page,
          limit,
          search,
          sortBy,
          sortDesc,
          activeTab: this.activeTab,
        };
      },
      openCollectionDialog() {
        this.collectionDialogOpen = true;
      },
      onCollectionBooked(result) {
        this.collectionBookedDryRun = result && result.dryRun === true;
        this.collectionBookedLinked = !!(result && result.linked);
        this.$apollo.queries.eligibleCollectionTestators.refetch();
        if (result && result.dryRunRequests) {
          this.dryRunPreviewRequests = result.dryRunRequests;
          // The booking dialog is closing in this same tick; opening the
          // preview now would stack its backdrop above itself.
          whenNoDialogIsActive(() => {
            this.dryRunPreviewOpen = true;
          });
        }
        const bookedIds = (result && result.bookedTestatorIds) || [];
        this.collectionBookedCount = bookedIds.length;
        this.collectionBookedSnackbar = true;
        const bookedIdSet = new Set(bookedIds.map(String));
        this.allTestators.forEach(row => {
          if (bookedIdSet.has(String(row.id))) {
            this.$set(row, 'willReadyToCollect', true);
          }
        });
      },
      stepsColor(numberOfCompletedSteps) {
        if (numberOfCompletedSteps > 4) return 'green';
        else if (numberOfCompletedSteps > 1) return 'orange';
        else return 'red';
      },
    },
    apollo: {
      eligibleCollectionTestators: {
        query: ADVISOR_COLLECTION_ELIGIBLE_TESTATORS_QUERY,
        update: data => data.advisorCollectionEligibleTestators || [],
        fetchPolicy: 'network-only',
        error(error) {
          console.error('Error fetching wills ready for collection:', error);
        },
      },
      allTestators: {
        query: ADVISOR_TESTATORS_OPTIMIZED_QUERY,
        variables() {
          return this.allTestatorsVariables;
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        result(response) {
          if (response.data && response.data.advisorTestatorsOptimized) {
            this.totalTestators =
              response.data.advisorTestatorsOptimized.totalTestators;
          }
        },
        error(error) {
          console.error('Apollo query error:', error);
          this.allTestatorsLoading = false;
          this.updateDatatableLoading();
        },
        update: data => data.advisorTestatorsOptimized.testators,
        watchLoading(isLoading) {
          this.allTestatorsLoading = isLoading;
          this.updateDatatableLoading();
        },
      },
      testatorActionCounts: {
        query: ADVISOR_TESTATOR_ACTION_COUNTS_OPTIMIZED_QUERY,
        update: data => data.advisorTestatorActionCountsOptimized,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        result(response) {
          if (
            response.data &&
            response.data.advisorTestatorActionCountsOptimized
          ) {
            const c = response.data.advisorTestatorActionCountsOptimized;
            this.actionCountsData = {
              complete_steps: c.completeSteps,
              download_wills: c.downloadWills,
              sign_upload_wills: c.signUploadWills,
              validated_wills: c.validatedWills,
              in_storage: c.inStorage,
            };
          }
        },
        error(error) {
          console.error('advisorTestatorActionCountsOptimized error:', error);
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .search-bar-container {
    padding-bottom: 30px;
  }
  .admin-list-table {
    margin: 0 32px 32px 32px;
  }
  .v-card {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .action-row {
    display: flex;
    align-items: center;
    height: 48px;
    padding-left: 8px;
    gap: 8px;

    .icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
    }

    .action-text {
      font-family: Montserrat;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: #333333;
    }
  }

  .custom-tabs {
    display: flex;
    width: auto;
    max-width: calc(100% - 64px);
    margin: 0 32px;
    background-color: #e8e8e8;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    align-items: center;
    border: none;
    box-sizing: border-box;
    margin-bottom: 10px;

    .custom-tab {
      padding: 24px 40px;
      height: 67px;
      font-weight: 600;
      font-size: 16px;
      line-height: 120%;
      color: #333;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #e8e8e8;
      transition: all 0.3s ease;
      border-right: 1px solid white;

      &:last-child {
        border-right: none;
      }

      @media (max-width: 1280px) {
        font-size: 13px;
      }

      &.active {
        color: white;
      }

      &.datatable-bg-light-green {
        background-color: #8dc63f;
      }
      &.datatable-bg-pink {
        background-color: #ed0080;
      }
      &.datatable-bg-blue {
        background-color: #00c0e8;
      }
      &.datatable-bg-orange {
        background-color: #f2a025;
      }
      &.datatable-bg-green {
        background-color: #00b188;
      }
      &.datatable-bg-in-storage {
        background-color: #333;
      }
    }
  }

  .v-data-table {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;

    ::v-deep {
      .v-data-table__wrapper {
        border-radius: 8px;
      }

      th {
        background: #f5f5f5 !important;
        font-family: Montserrat !important;
        font-weight: 600 !important;
        font-size: 14px !important;
        line-height: 19px !important;
        letter-spacing: 0px !important;
        vertical-align: middle !important;
        color: #222 !important;
        border-top-left-radius: 0 !important;
        border-top-right-radius: 0 !important;
        padding: 20px !important;
      }

      td {
        height: 60px !important;
      }
    }
  }

  .action-cell {
    display: flex;
    align-items: center;

    .v-icon {
      margin-right: 8px;
    }
  }

  .search-field {
    .v-input__slot {
      border-radius: 24px !important;
      padding: 0 16px;
    }
  }
  .datatable-color-light-green {
    color: #8dc63f;
  }
  .datatable-bg-light-green {
    background-color: #8dc63f;
  }

  .datatable-color-pink {
    color: #ed0080;
  }
  .datatable-bg-pink {
    background-color: #ed0080;
  }

  .datatable-color-blue {
    color: #00c0e8;
  }
  .datatable-bg-blue {
    background-color: #00c0e8;
  }

  .datatable-color-orange {
    color: #f2a025;
  }
  .datatable-bg-orange {
    background-color: #f2a025;
  }

  .datatable-color-green {
    color: #00b188;
  }
  .datatable-bg-green {
    background-color: #00b188;
  }

  ::v-deep .v-tab.datatable-bg-light-green {
    background-color: #8dc63f !important;
    color: #fff !important;
  }

  ::v-deep .v-data-table {
    table {
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
    }
    thead tr th {
      border-bottom: 1px solid #e0e0e0 !important;
      border-right: 1px solid #e0e0e0 !important;
      &:last-child {
        border-right: none !important;
      }
    }
    tbody tr {
      border-bottom: 1px solid #e0e0e0;
      &:last-child {
        border-bottom: none;
      }
      &:hover {
        background-color: #f5f5f5 !important;
      }
    }
    tbody tr td {
      border-right: 1px solid #e0e0e0;
      &:last-child {
        border-right: none;
      }
      padding: 0 16px !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  ::v-deep .v-data-table,
  ::v-deep .v-data-table__wrapper,
  ::v-deep .v-data-table thead th:first-child,
  ::v-deep .v-data-table thead th:last-child {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }

  .email-link {
    color: inherit;
    text-decoration: none;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  ::v-deep th {
    white-space: normal !important;
    word-break: keep-all !important;
    overflow-wrap: normal !important;
    min-width: 100px;
  }
  ::v-deep th .v-data-table-header__icon {
    display: block !important;
    margin: 0 auto !important;
    position: static !important;
  }
  ::v-deep th .v-data-table-header__content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
</style>
